import type { ReactNode } from "react";
import { useCallback, useMemo, use, createContext } from "react";

import { swSettings } from "app/common/services/swSettings";
import { useAvailableProducts } from "app/components/hooks/useAvailableProducts";
import {
    hasAddonsEnabled,
    isTraditionalTrialOrAfterTrialUser,
} from "app/UtilitiesAndConstants/UtilityFunctions/usersUtils";

import { AddOnType } from "./const";

interface IAddOnData {
    addonId?: string | null;
    isPurchased?: boolean;
    isUpgradeAvailable?: boolean;
}

const isAddOnType = (value: string): value is AddOnType => {
    return (Object.values(AddOnType) as string[]).includes(value);
};

export interface IAddonStateContext {
    hasAddonsHub: boolean;
    getAddon: (addonType: AddOnType) => string | null | undefined;
    getIsAddonAvailable: (addonType: AddOnType) => boolean;
    getIsAddonUpgradable: (addonType: AddOnType) => boolean;
    canBuyAtLeastOneAddon: boolean;
    isAddonsDataLoading: boolean;
}

const AddonStateContext = createContext<IAddonStateContext | undefined>(undefined);

interface IAddonStateProviderProps {
    children: ReactNode;
}

export const AddonStateProvider = ({ children }: IAddonStateProviderProps) => {
    const isAddonsEnabled = hasAddonsEnabled(swSettings);
    const isAvailableProductsEnabled = isAddonsEnabled || isTraditionalTrialOrAfterTrialUser(swSettings);

    const { addOnTypes, addOnAvailabilities, isError, isLoading } = useAvailableProducts({
        enabled: isAvailableProductsEnabled,
    });

    const addons = useMemo((): Map<AddOnType, IAddOnData> => {
        const addonsMap = new Map<AddOnType, IAddOnData>();

        if (!isAvailableProductsEnabled || addOnTypes.length === 0 || isLoading || isError) {
            return addonsMap;
        }

        addOnTypes.filter(isAddOnType).forEach((addonType) => {
            const availability = addOnAvailabilities[addonType];
            const isPurchased = availability?.isPurchased ?? false;

            addonsMap.set(addonType, {
                addonId: isPurchased ? addonType : null,
                isPurchased,
                isUpgradeAvailable: availability?.isUpgradeAvailable ?? false,
            });
        });

        return addonsMap;
    }, [addOnTypes, addOnAvailabilities, isAvailableProductsEnabled, isError, isLoading]);

    /**
     * Use this function to get the addon state for the user. Return is either
     * - A string representing the addon name
     * - `null` if the user _can_ purchase the addon but doesn't have it yet
     * - `undefined` if the user cannot buy the specific addon type
     */
    const getAddon = useCallback(
        (addonType: AddOnType): string | null | undefined => addons.get(addonType)?.addonId,
        [addons],
    );

    /**
     * Returns a simple boolean that tells if the user can get a specific addon, either by
     * purchasing it (not purchased yet) or upgrading it (already purchased but upgradeable).
     * If the user has the addon and can't upgrade it, or can't buy it at all, it'll be `false`.
     */
    const getIsAddonAvailable = useCallback(
        (addonType: AddOnType): boolean => {
            const addon = addons.get(addonType);
            if (!addon) {
                return false;
            }

            return !addon.isPurchased || !!addon.isUpgradeAvailable;
        },
        [addons],
    );

    /**
     * Returns whether an already-purchased addon can be upgraded.
     */
    const getIsAddonUpgradable = useCallback(
        (addonType: AddOnType): boolean => !!addons.get(addonType)?.isUpgradeAvailable,
        [addons],
    );

    const canBuyAtLeastOneAddon = useMemo(
        () => Object.values(AddOnType).some(getIsAddonAvailable),
        [getIsAddonAvailable],
    );

    return (
        <AddonStateContext.Provider
            value={{
                hasAddonsHub: isAddonsEnabled,
                getAddon,
                getIsAddonAvailable,
                getIsAddonUpgradable,
                canBuyAtLeastOneAddon,
                isAddonsDataLoading: isLoading,
            }}
        >
            {children}
        </AddonStateContext.Provider>
    );
};

export const useAddonsState = (): IAddonStateContext => {
    const ctx = use(AddonStateContext);

    if (!ctx) {
        throw new Error("useAddonState must be used within AddonStateProvider");
    }

    return ctx;
};
